var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

/* eslint react/prop-types: 0 */
import * as R from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { getDescendants, getHierarchicalId } from './utils';

export var spotlightFilter = function spotlightFilter(term) {
  var labelRenderer = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : R.prop('label');
  var displayChildren = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return function (items) {
    if (R.isEmpty(term) || R.isNil(term)) {
      return items;
    }

    var groupedItemsByParentId = R.groupBy(R.propOr('#ROOT', 'parentId'), items);

    var findParents = function findParents(item) {
      var acc = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

      var parentId = R.prop('parentId', item);
      if (parentId && parentId !== '#ROOT') {
        var parent = R.find(R.either(R.propEq('id', parentId), R.propEq('hierarchicalId', parentId)), items);
        if (parent) {
          return findParents(parent, [parent].concat(acc));
        }
      }
      return acc;
    };
    var matchRegExp = function matchRegExp(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    // Highlight matching terms in the label
    var highlightLabel = function highlightLabel(label) {
      var regex = new RegExp('(' + matchRegExp(term) + ')', 'gi');
      return label.replace(regex, function (match) {
        return '<span style=background-color:yellow;border-bottom:2px solid #8CC841>' + match + '</span>';
      });
    };

    var recurse = function recurse(item) {
      var label = labelRenderer(item);
      var isValidItem = function isValidItem(item) {
        return R.includes(R.toLower(term), R.toLower(item));
      };

      var result = [];

      if (isValidItem(label)) {
        var highlightedLabel = highlightLabel(label);
        var parents = findParents(item);
        result = [].concat(parents, [_extends({}, item, { highlightedLabel: highlightedLabel })]);

        if (displayChildren) {
          var children = getDescendants(getHierarchicalId(item), groupedItemsByParentId);
          var highlightedChildren = R.map(function (child) {
            var labelChild = labelRenderer(child);
            return isValidItem(labelChild) ? _extends({}, child, { highlightedLabel: highlightLabel(labelChild) }) : child;
          })(children);
          return [].concat(parents, [_extends({}, item, { highlightedLabel: highlightedLabel })], highlightedChildren);
        }
      }

      if (R.has(getHierarchicalId(item), groupedItemsByParentId)) {
        var childItems = groupedItemsByParentId[getHierarchicalId(item)] || [];
        var matchingChildren = R.chain(recurse, childItems);

        // res with any matching children
        return R.uniq([].concat(result, matchingChildren));
      }
      return result;
    };

    return R.uniqBy(getHierarchicalId)(R.chain(recurse, groupedItemsByParentId['#ROOT'] || []));
  };
};

var withSpotlight = function withSpotlight(Component) {
  return function (_ref) {
    var items = _ref.items,
        defaultSpotlight = _ref.defaultSpotlight,
        labelRenderer = _ref.labelRenderer,
        displayChildren = _ref.displayChildren,
        rest = _objectWithoutProperties(_ref, ['items', 'defaultSpotlight', 'labelRenderer', 'displayChildren']);

    var _useState = useState({ term: '' }),
        term = _useState[0].term,
        setSpotlight = _useState[1];

    useEffect(function () {
      if (!R.isNil(defaultSpotlight) && !R.isEmpty(defaultSpotlight)) {
        setSpotlight(defaultSpotlight);
      }
    }, [defaultSpotlight]);

    var filteredItems = useMemo(function () {
      return spotlightFilter(term, labelRenderer, displayChildren)(items);
    }, [term, items, labelRenderer]);

    return React.createElement(Component, _extends({}, rest, {
      allItems: items,
      items: filteredItems,
      labelRenderer: labelRenderer,
      hasSpotlight: R.length(items) >= 8,
      spotlight: { term: term },
      setSpotlight: setSpotlight
    }));
  };
};
export { withSpotlight };